import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

// Global styles
import './styles.css';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Add global CSS
const style = document.createElement('style');
style.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  body {
    font-family: 'Montserrat', sans-serif;
    background-color: #000;
    color: #fff;
    overflow-x: hidden;
    line-height: 1.6;
  }

  .app-container {
    position: relative;
    width: 100%;
    min-height: 100vh;
  }

  /* Navbar Styles */
  .navbar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 1000;
    padding: 1.5rem 0;
    transition: background-color 0.3s ease;
    background-color: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }

  .nav-container {
    width: 90%;
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .nav-logo {
    font-size: 1.5rem;
    font-weight: 600;
    color: #fff;
    text-decoration: none;
    letter-spacing: 1px;
  }

  .nav-links {
    display: flex;
    gap: 2rem;
  }

  .nav-link {
    color: #fff;
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 500;
    letter-spacing: 1px;
    transition: opacity 0.3s ease;
  }

  .nav-link:hover {
    opacity: 0.8;
  }

  .cart-link {
    position: relative;
  }

  .cart-count {
    display: inline-block;
    margin-left: 5px;
  }

  /* Video Background Styles */
  .video-scenery,
  .video-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    overflow: hidden;
    z-index: -1;
  }

  .scenery-video,
  .background-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
  }

  /* Main Content Styles */
  .main-content {
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.8s ease, transform 0.8s ease;
  }

  .main-content.loaded {
    opacity: 1;
    transform: translateY(0);
  }
  
  .main-content.video-fallback .video-scenery,
  .main-content.video-fallback .video-background {
    background: linear-gradient(135deg, #8e2de2, #4a00e0);
  }
  
  /* Always have a background even while video is loading */
  .video-scenery,
  .video-background {
    background: linear-gradient(135deg, #8e2de2, #4a00e0);
  }

  .content-wrapper {
    width: 90%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 2;
  }

  .hero-text {
    margin-bottom: 2rem;
  }
  
  .brand-label {
    display: inline-block;
    font-size: 0.9rem;
    font-weight: 500;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.3);
    padding: 0.4rem 1rem;
    border-radius: 30px;
  }

  .big-text {
    font-size: 4.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    line-height: 1.1;
    text-transform: uppercase;
  }

  .subtitle {
    font-size: 1.2rem;
    font-weight: 400;
    color: #f8f8f8;
    max-width: 500px;
  }

  .hero-cta {
    margin-top: 2rem;
  }

  .cta-button {
    padding: 0.75rem 2rem;
    background-color: #fff;
    color: #000;
    border: none;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 1px;
    cursor: pointer;
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  .cta-button:hover {
    background-color: rgba(255, 255, 255, 0.9);
  }

  /* Responsive Styles */
  @media (max-width: 768px) {
    .nav-container {
      width: 95%;
    }
    
    .nav-links {
      gap: 1.2rem;
    }
    
    .big-text {
      font-size: 3rem;
    }
    
    .subtitle {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    .nav-logo {
      font-size: 1.2rem;
    }
    
    .nav-links {
      gap: 0.8rem;
    }
    
    .nav-link {
      font-size: 0.75rem;
    }
    
    .big-text {
      font-size: 2.5rem;
    }
    
    .content-wrapper {
      padding: 0 1rem;
    }
    
    .cta-button {
      padding: 0.75rem 1.5rem;
    }
  }
  
  /* Shop Section Styles */
  .about-section {
    padding: 6rem 2rem;
    min-height: 100vh;
    background-color: #f8f8f8;
    color: #000;
  }
  
  .about-container {
    max-width: 1400px;
    margin: 0 auto;
  }
  
  .section-title {
    font-size: 2rem;
    font-weight: 600;
    margin-bottom: 3rem;
    text-align: center;
    letter-spacing: 2px;
  }
  
  .product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 2.5rem;
  }
  
  .product-card {
    background-color: #fff;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  
  .product-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
  
  .product-image {
    height: 280px;
    overflow: hidden;
  }
  
  .product-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  .product-card:hover .product-image img {
    transform: scale(1.05);
  }
  
  .product-info {
    padding: 1.5rem;
  }
  
  .product-title {
    font-size: 1.2rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }
  
  .product-price {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 1.2rem;
  }
  
  .add-to-cart {
    width: 100%;
    padding: 0.75rem 0;
    background-color: #000;
    color: #fff;
    border: none;
    font-size: 0.9rem;
    font-weight: 500;
    letter-spacing: 1px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  .add-to-cart:hover {
    background-color: #333;
  }
  
  @media (max-width: 768px) {
    .product-grid {
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 1.5rem;
    }
    
    .product-image {
      height: 220px;
    }
    
    .section-title {
      font-size: 1.8rem;
      margin-bottom: 2rem;
    }
  }
  
  @media (max-width: 480px) {
    .about-section {
      padding: 5rem 1rem;
    }
    
    .product-grid {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    
    .section-title {
      font-size: 1.5rem;
    }
  }

  /* Contact Section Styles */
  .contact-section {
    padding: 6rem 2rem;
    min-height: 100vh;
    background-color: #f8f8f8;
    color: #000;
  }
  
  .contact-container {
    max-width: 1200px;
    margin: 0 auto;
  }
  
  .contact-content {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 3rem;
  }
  
  .contact-info {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }
  
  .info-item {
    margin-bottom: 1.5rem;
  }
  
  .info-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.8rem;
    color: #000;
  }
  
  .info-text {
    font-size: 0.95rem;
    color: #333;
    line-height: 1.6;
  }
  
  .contact-form-container {
    background-color: #fff;
    padding: 2.5rem;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  }
  
  .contact-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .form-group label {
    font-size: 0.9rem;
    font-weight: 500;
    color: #333;
  }
  
  .form-group input,
  .form-group textarea {
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-family: inherit;
    font-size: 0.95rem;
    color: #333;
  }
  
  .form-group textarea {
    resize: vertical;
  }
  
  .form-group input:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: #aaa;
  }
  
  .submit-button {
    padding: 0.9rem 0;
    background-color: #000;
    color: #fff;
    border: none;
    border-radius: 5px;
    font-size: 0.9rem;
    font-weight: 500;
    letter-spacing: 1px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    margin-top: 0.5rem;
  }
  
  .submit-button:hover {
    background-color: #333;
  }
  
  @media (max-width: 768px) {
    .contact-content {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    
    .contact-info {
      order: 2;
    }
    
    .contact-form-container {
      order: 1;
    }
  }

  /* Projects Section Styles */
  .projects-section {
    padding: 6rem 2rem;
    min-height: 100vh;
    background-color: #f8f8f8;
    color: #000;
  }
  
  .projects-container {
    max-width: 1200px;
    margin: 0 auto;
  }
  
  .projects-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 2.5rem;
    margin-top: 3rem;
  }
  
  .project-card {
    background-color: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  
  .project-card:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
  
  .project-image {
    height: 200px;
    overflow: hidden;
  }
  
  .project-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  .project-card:hover .project-image img {
    transform: scale(1.05);
  }
  
  .project-content {
    padding: 1.5rem;
  }
  
  .project-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    color: #222;
  }
  
  .project-description {
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }
  
  .project-technologies {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
  
  .tech-tag {
    font-size: 0.75rem;
    padding: 0.3rem 0.6rem;
    background-color: #f0f0f0;
    color: #555;
    border-radius: 20px;
  }
  
  .project-links {
    display: flex;
    gap: 1rem;
  }
  
  .project-link {
    color: #333;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: color 0.3s ease;
    position: relative;
  }
  
  .project-link:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background-color: #333;
    transition: width 0.3s ease;
  }
  
  .project-link:hover {
    color: #000;
  }
  
  .project-link:hover:after {
    width: 100%;
  }
  
  /* Resume Section Styles */
  .resume-section {
    padding: 6rem 2rem;
    min-height: 100vh;
    background-color: #f8f8f8;
    color: #000;
  }
  
  .resume-container {
    max-width: 1000px;
    margin: 0 auto;
  }
  
  .resume-actions {
    display: flex;
    justify-content: center;
    margin-bottom: 3rem;
  }
  
  .download-button {
    padding: 0.8rem 2rem;
    background-color: #333;
    color: white;
    border: none;
    border-radius: 5px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.3s ease;
    text-decoration: none;
    display: inline-block;
  }
  
  .download-button:hover {
    background-color: #222;
  }
  
  .resume-preview {
    background-color: white;
    padding: 3rem;
    border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  }
  
  .resume-header {
    text-align: center;
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #eee;
  }
  
  .resume-name {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: #222;
  }
  
  .resume-contact {
    font-size: 0.9rem;
    color: #555;
    line-height: 1.5;
  }
  
  .resume-objective {
    font-size: 0.95rem;
    color: #444;
    margin-bottom: 1rem;
    line-height: 1.6;
  }
  
  .resume-section-content {
    margin-bottom: 3rem;
  }
  
  .resume-section-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #f0f0f0;
    color: #222;
  }
  
  .resume-item {
    margin-bottom: 2rem;
  }
  
  .resume-item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }
  
  .resume-item-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
  }
  
  .resume-item-date {
    font-size: 0.9rem;
    color: #777;
  }
  
  .resume-item-subtitle {
    font-size: 1rem;
    color: #555;
    margin-bottom: 0.75rem;
  }
  
  .resume-item-details {
    padding-left: 1.5rem;
    margin: 0.75rem 0;
  }
  
  .resume-item-details li {
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    color: #666;
  }
  
  .skills-columns {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 2rem;
  }
  
  .skills-category {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #333;
  }
  
  .skills-list {
    padding-left: 1.5rem;
    margin: 0;
  }
  
  .skills-list li {
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    color: #666;
  }
  
  /* About Section Updates */
  .about-content {
    background-color: white;
    padding: 3rem;
    border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
  }
  
  .about-image-container {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;
  }
  
  .about-image {
    width: 100%;
    max-width: 600px;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    object-fit: cover;
  }
  
  .about-text {
    margin-bottom: 3rem;
  }
  
  .bio-text {
    font-size: 1rem;
    color: #555;
    line-height: 1.8;
    margin-bottom: 1.5rem;
  }
  
  .skills-section {
    margin-bottom: 3rem;
  }
  
  .skills-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #f0f0f0;
    color: #222;
  }
  
  .skills-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 2rem;
  }
  
  .skill-category {
    margin-bottom: 1.5rem;
  }
  
  .category-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #333;
  }
  
  .skill-list {
    list-style-type: none;
    padding: 0;
  }
  
  .skill-item {
    font-size: 0.9rem;
    color: #555;
    margin-bottom: 0.5rem;
    padding: 0.3rem 0;
  }
  
  .experience-section {
    margin-top: 3rem;
  }
  
  .experience-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #f0f0f0;
    color: #222;
  }
  
  .timeline {
    position: relative;
    padding-left: 2rem;
  }
  
  .timeline:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 2px;
    background-color: #f0f0f0;
  }
  
  .timeline-item {
    position: relative;
    padding-bottom: 2.5rem;
  }
  
  .timeline-item:last-child {
    padding-bottom: 0;
  }
  
  .timeline-item:before {
    content: '';
    position: absolute;
    left: -2rem;
    top: 0.3rem;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: #333;
  }
  
  .timeline-date {
    font-size: 0.9rem;
    color: #777;
    margin-bottom: 0.5rem;
  }
  
  .timeline-content {
    position: relative;
  }
  
  .job-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
  }
  
  .job-description {
    font-size: 0.9rem;
    color: #555;
    line-height: 1.6;
  }
  
  /* Social Links */
  .social-links {
    display: flex;
    gap: 1rem;
    margin-top: 0.5rem;
  }
  
  .social-link {
    color: #333;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: color 0.3s ease;
    position: relative;
  }
  
  .social-link:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background-color: #333;
    transition: width 0.3s ease;
  }
  
  .social-link:hover {
    color: #000;
  }
  
  .social-link:hover:after {
    width: 100%;
  }
  
  /* Responsive Updates */
  @media (max-width: 768px) {
    .projects-grid {
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    }
    
    .resume-preview {
      padding: 2rem;
    }
    
    .skills-columns {
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    }
    
    .skills-grid {
      grid-template-columns: 1fr;
    }
    
    .timeline {
      padding-left: 1.5rem;
    }
    
    .timeline-item:before {
      left: -1.5rem;
    }
    
    .about-content {
      grid-template-columns: 1fr;
    }
    
    .about-image-container {
      margin-bottom: 2rem;
    }
    
    .about-image {
      max-width: 500px;
    }
  }
  
  @media (max-width: 480px) {
    .projects-grid {
      grid-template-columns: 1fr;
    }
    
    .project-image {
      height: 180px;
    }
    
    .resume-preview {
      padding: 1.5rem;
    }
    
    .resume-item-header {
      flex-direction: column;
      align-items: flex-start;
    }
    
    .resume-item-date {
      margin-top: 0.25rem;
    }
    
    .skills-columns {
      grid-template-columns: 1fr;
    }
    
    .timeline {
      padding-left: 1.25rem;
    }
    
    .timeline-item:before {
      left: -1.25rem;
      width: 10px;
      height: 10px;
    }
    
    .about-image {
      max-width: 400px;
    }
  }
`;
document.head.appendChild(style);
